<template>
  <MglMap
    container="basemap"
    hash
    :access-token="mapAccessToken"
    :mapbox-gl="mapbox"
    :attribution-control="false"
    :box-zoom="boxZoom"
    :max-bounds="maxBounds"
    :drag-rotate="false"
    :fade-duration="100"
    :map-style="mapStyle"
    :center="defaultCenter"
    :zoom="defaultZoom"
    :transform-request="transformRequest"
    :logo-position="mapboxLogoPosition"
    @load="onMapLoaded"
    @move="onMove"
    v-on="$listeners"
    v-bind="$props"
  >
    <slot
      v-if="shouldRenderLayers"
      v-bind="slotProps"
      name="layers"
      :selected-basemap="selectedBasemap"
      :set-selected-basemap="setBasemap"
      :selected-basemap-is-mapbox-basemap="isMapboxBasemapSelected"
    />
    <slot
      v-bind="slotProps"
      :selected-basemap="selectedBasemap"
      :set-selected-basemap="setBasemap"
      :selected-basemap-is-mapbox-basemap="isMapboxBasemapSelected"
    />
    <map-controls
      :use-mobile-map-controls="mobileMapControls"
      :is-max-zoom="isMaxZoom"
      :is-min-zoom="isMinZoom"
      :is-default-view="isDefaultView"
      :basemaps="basemaps"
      :selected-basemap="selectedBasemap"
      :rounded="roundedControls"
      @zoom-in="zoomIn"
      @zoom-out="zoomOut"
      @reset-view="resetView"
      @set-basemap="setBasemap"
    />
  </MglMap>
</template>

<script>
import mapboxgl from 'mapbox-gl';
import mapboxUtils from 'mapbox-gl-utils';
import _debounce from 'lodash.debounce';
import _isEqual from 'lodash.isequal';
import { MglMap } from 'v-mapbox';
import { addHighlightSource, highlightSingleFeature } from '@/util/layers';
import {
  MAP_DEFAULTS,
  MAP_STYLE_DICT,
  AVAILABLE_MAPBOX_BASEMAPS,
  SORT_ANCHOR_SOURCE,
  AFTER_FEATURES_LAYER,
  AFTER_HIGHLIGHTS_LAYER,
  AFTER_LABELS_LAYER,
} from '../constants';
import { decrementZoomInTileUrl } from '../util/tiles';
import MapControls from './MapControls/MapControls.vue';

export default {
  name: 'vetro-map',
  components: {
    MglMap,
    MapControls,
  },
  props: {
    // Mapbox GL API access token
    mapAccessToken: { type: String, required: true },
    // Should `basemaps` be an array of a Basemap type that could have { name, link, maxZoom, etc. }?
    basemaps: {
      type: Array,
      default: () => MAP_DEFAULTS.BASEMAPS.map((name) => ({ name, disabled: false })),
    },
    // At the time of writing, we avoid changing these later, as changes will update
    //  the GlMap component and we prefer to not get in the way of Mapbox's own zoom/pan handling
    defaultBasemap: { type: String, default: MAP_DEFAULTS.STYLE },
    boxZoom: { type: Boolean, default: false },
    defaultCenter: { type: Array, default: () => MAP_DEFAULTS.INITIAL_CENTER },
    maxBounds: { type: Array, default: undefined },
    defaultZoom: { type: Number, default: MAP_DEFAULTS.INITIAL_ZOOM },
    // Tile server base URL (including scheme) we should use for rendering internal Vetro layers
    tileServers: { type: Array, default: () => [], required: true },
    // Attached to tile request url if using token auth
    vetroApiToken: { type: String, required: false },
    // If true, the map controls will be displayed in the top left (with a truncated basemap
    // selector button) instead of in the default bottom right position.
    mobileMapControls: { type: Boolean, default: false },
    highlightedFeature: { type: Object, required: false },
    authCloudFrontDomains: { type: Array, default: () => [], required: false },
    roundedControls: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentZoom: this.defaultZoom,
      currentCenter: this.defaultCenter,
      mapbox: mapboxgl,
      selectedBasemap: this.defaultBasemap,
      shouldRenderLayers: true,
    };
  },
  computed: {
    mapStyle() {
      // There are two types of basemap, mapbox-provided vector basemaps, and non-mapbox raster (e.g., satellite) basemaps.
      // The raster basemaps will still have a mapbox basemap underneath them. To avoid loading vector tiles from mapbox while
      // a raster basemap is selected, we set the map style to 'none' when a raster basemap is selected.
      return MAP_STYLE_DICT[this.selectedBasemap] ?? MAP_STYLE_DICT.none;
    },
    isMaxZoom() {
      return this.currentZoom === this.map?.getMaxZoom();
    },
    isMinZoom() {
      // The minimum possible zoom level may be greater than what is returned by getMinZoom()
      // on larger displays. To account for this, we also check to see if latitudes -85 - 85
      // fit within our current map bounds - which will only be the case if we are zoomed out
      // as far as possible, but cannot reach minZoom.
      // https://github.com/mapbox/mapbox-gl-js/issues/9172
      const currentZoomIsMin = this.currentZoom === this.map?.getMinZoom();
      const top = this.map?.getBounds()._ne.lat;
      const bottom = this.map?.getBounds()._sw.lat;
      const mapFitsAll = top >= 85 && bottom <= -85;

      return currentZoomIsMin || mapFitsAll;
    },
    isDefaultView() {
      return this.zoom === this.defaultZoom && _isEqual(this.defaultCenter, this.currentCenter);
    },
    isMapboxBasemapSelected() {
      // True if the currently selected Basemap is a Mapbox-provided one, false if a custom raster basemap
      return (
        this.selectedBasemap && AVAILABLE_MAPBOX_BASEMAPS.some((b) => b === this.selectedBasemap)
      );
    },
    transformRequest() {
      return (url) => {
        if (url.includes('?decrementZoom=true')) {
          const withoutFlag = url.replace(/\?decrementZoom=true$/, '');
          // see decrementZoomInTileUrl function for reasoning
          return { url: decrementZoomInTileUrl(withoutFlag) };
        }

        const options = { headers: {} };

        const matchesTileServers = this.tileServers.some((tileServer) =>
          url.includes(`${tileServer}/maps`),
        );
        const matchesCloudFrontDomains = this.authCloudFrontDomains.some((cfd) =>
          url.includes(cfd),
        );

        if (matchesTileServers || matchesCloudFrontDomains) {
          options.credentials = 'include';

          if (this.vetroApiToken) options.headers.token = this.vetroApiToken;
        }

        return {
          url,
          ...options,
        };
      };
    },
    slotProps() {
      return {
        selectedBasemap: this.selectedBasemap,
        setSelectedBasemap: this.setBasemap,
        selectedBasemapIsMapboxBasemap: this.isMapboxBasemapSelected,
      };
    },
    mapboxLogoPosition() {
      return this.mobileMapControls ? 'top-right' : 'bottom-right';
    },
  },
  created() {
    this.map = null;
  },
  watch: {
    mapStyle() {
      // V2 (maybe other uses in the future?) needs to respond to Mapbox style changes
      //  by refreshing their sources and layers
      // We should wait until the map actually loads the new style so that we don't
      //  react too fast to the user's action
      this.map.once('style.load', () => {
        this.$emit('map-style-changed');

        // reload layers when the basemap changes, else the map's new style will no
        // longer contain them
        this.shouldRenderLayers = false;
        this.$nextTick(() => {
          addHighlightSource(this.map);
          this.addSortAnchorLayers(this.map);
          this.shouldRenderLayers = true;
        });
      });
    },
    highlightedFeature(feature) {
      highlightSingleFeature(this.map, feature);
    },
  },
  methods: {
    addSortAnchorLayers(map) {
      map.U.addGeoJSON(SORT_ANCHOR_SOURCE);

      // We want to render mapbox layers in the order of ALL features, ALL highlights,
      // and finally ALL labels. These empty layers serve as ordering 'anchors' which
      // each respective layer type can be appended before, within VetroFeatureLayer.
      map.U.addLayer({ id: AFTER_FEATURES_LAYER, type: 'symbol', source: SORT_ANCHOR_SOURCE });
      map.U.addLayer({ id: AFTER_HIGHLIGHTS_LAYER, type: 'symbol', source: SORT_ANCHOR_SOURCE });
      map.U.addLayer({ id: AFTER_LABELS_LAYER, type: 'symbol', source: SORT_ANCHOR_SOURCE });
    },
    async onMapLoaded({ map }) {
      map.touchZoomRotate.disableRotation();

      this.map = map;
      map.getCanvas().classList.add('vetro-map');
      document.querySelector('.vetro-map').focus();

      if (window.Cypress) {
        window.map = map;
      }

      mapboxUtils.init(map, mapboxgl);

      addHighlightSource(map);

      this.addSortAnchorLayers(map);

      this.$emit('map-loaded', map);
    },
    onMove: _debounce(function debouncedMove({ map }) {
      this.currentZoom = map.getZoom();
      this.currentCenter = map.getCenter();
    }, 200),
    setBasemap(basemap) {
      this.selectedBasemap = basemap;
      this.$emit('selected-basemap-changed', basemap);
    },
    zoomIn() {
      this.map.zoomIn();
    },
    zoomOut() {
      this.map.zoomOut();
    },
    resetView() {
      this.map.jumpTo({
        center: this.defaultCenter,
        zoom: this.defaultZoom,
      });
    },
  },
};
</script>
